import { memo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons/faSpinner';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Button } from 'Button';
import { twMerge } from 'tailwind-merge';
import { Text } from 'Text';
import { Input } from 'Input';
import { type LearnerProject } from 'LearnerProjects';
import { Trans, useTranslation } from 'react-i18next';
import { FormProvider, useForm, yupResolver } from 'FrontRoyalReactHookForm';
import * as Yup from 'yup';
import './locales/create_project_group_modal_page-en.json';
import { useCurrentUser } from 'FrontRoyalAngular';
import { Icon } from 'FrontRoyalIcon';
import { getCohort } from 'Users';
import { getBrandName } from 'AppBranding';
import { getMemberDetailsFromMembershipUserAttrs } from './getMemberDetails';
import { MembersList } from './MembersList';
import { type MemberDetails, MemberDetailsMembershipStatus } from './ProjectGroup.types';
import { projectGroupsApi } from './projectGroupsApi';

type CreateProjectGroupFormValues = {
    search: string;
};

const CreateProjectGroupFormSchema = Yup.object()
    .shape({
        search: Yup.string().required(),
    })
    .required();

const Description = ({ project }: { project: LearnerProject }) => {
    const { t } = useTranslation('back_royal');
    const currentUser = useCurrentUser();

    return (
        <div className="my-0">
            <Text color="black">
                <Trans
                    i18nKey="create_project_group_modal_page.description"
                    t={t}
                    components={{
                        strong: <span className="font-semibold" />,
                    }}
                    values={{
                        projectTitle: project.title,
                        maxGroupSize: project.maxGroupSize,
                        institutionName: getBrandName(currentUser),
                    }}
                />
            </Text>
        </div>
    );
};

const MemberSearch = ({
    className,
    maxGroupSizeReached,
    searching,
    searchError,
    onChange,
    onSearch,
}: {
    className?: string;
    maxGroupSizeReached: boolean;
    searching: boolean;
    searchError: { msg: string; type: 'error' | 'warning' } | null;
    onChange: () => void;
    onSearch: () => Promise<void>;
}) => {
    const { t } = useTranslation('back_royal');

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !searching && !maxGroupSizeReached) {
            e.preventDefault();
            onSearch();
        }
    };

    return (
        <div className={twMerge('flex flex-col gap-2', className)}>
            <div className="flex gap-2.5">
                <Input
                    disabled={maxGroupSizeReached}
                    name="search"
                    placeholder={t('create_project_group_modal_page.member_email_input_placeholder') as string}
                    onChange={onChange}
                    onKeyDown={handleKeyDown}
                    startContent={<Icon iconName="Search24" className="text-default-500" />}
                    classNames={{
                        inputWrapper: 'bg-slate-grey-50 hover:bg-slate-grey-100 rounded-7 h-12.5',
                        input: 'group-data-[has-value=true]:text-black',
                    }}
                />
                <Button
                    variant="solid"
                    color="black"
                    isDisabled={searching || maxGroupSizeReached}
                    onPress={onSearch}
                    icon={searching ? <FontAwesomeIcon icon={faSpinner} spin /> : undefined}
                >
                    {searching
                        ? t('create_project_group_modal_page.add_member_button_searching_label')
                        : t('create_project_group_modal_page.add_member_button_label')}
                </Button>
            </div>
            {searchError && (
                <div
                    className={`flex items-center gap-1 ${
                        searchError.type === 'warning' ? 'text-amber-600' : 'text-red-600'
                    }`}
                >
                    {searchError.type === 'warning' && <FontAwesomeIcon icon={faCircleInfo} />}
                    <Text variant="small" className="text-inherit" weight="medium">
                        {searchError.msg}
                    </Text>
                </div>
            )}
        </div>
    );
};

const FormSubmissionButtons = ({
    className,
    members,
    onCancel,
    onConfirm,
}: {
    className?: string;
    members: MemberDetails[];
    onCancel: () => void;
    onConfirm: () => void;
}) => {
    const { t } = useTranslation('back_royal');
    return (
        <div className={twMerge('flex flex-col gap-3 xs:flex-row xs:justify-center xs:gap-7.5', className)}>
            <Button className="font-normal" variant="light" onPress={onCancel}>
                {t('create_project_group_modal_page.cancel_button_label')}
            </Button>
            <Button variant="solid" color="black" isDisabled={members.length <= 1} onPress={onConfirm}>
                {t('create_project_group_modal_page.confirm_button_label')}
            </Button>
        </div>
    );
};

// oxlint-disable-next-line max-lines-per-function
const CreateProjectGroupFormComponent = ({
    project,
    members,
    setMembers,
    onCancel,
    onConfirm,
}: {
    project: LearnerProject;
    members: MemberDetails[];
    setMembers: (members: MemberDetails[]) => void;
    onCancel: () => void;
    onConfirm: () => void;
}) => {
    const formFunctions = useForm<CreateProjectGroupFormValues>({
        defaultValues: { search: '' },
        resolver: yupResolver(CreateProjectGroupFormSchema),
        mode: 'all',
    });
    const { setFieldValue, watch } = formFunctions;
    const { t } = useTranslation('back_royal');
    const currentUser = useCurrentUser();
    const [searchError, setSearchError] = useState<{ msg: string; type: 'error' | 'warning' } | null>(
        members.length >= project.maxGroupSize!
            ? { msg: t('create_project_group_modal_page.max_group_size_reached_warning_msg'), type: 'warning' }
            : null,
    );
    const [searching, setSearching] = useState(false);

    const onSearch = async () => {
        const search = watch('search').trim();
        if (!search) {
            return;
        }
        if (members.length >= project.maxGroupSize!) {
            setSearchError({
                msg: t('create_project_group_modal_page.max_group_size_reached_warning_msg'),
                type: 'warning',
            });
            return;
        }

        setSearching(true);

        let response;
        try {
            response = await projectGroupsApi.makeRequest('memberSearch', {
                search,
                cohort_id: getCohort(currentUser)!.id,
                requirement_identifier: project.requirementIdentifier,
            });
        } catch (error) {
            const err = error as FetchBaseQueryError;
            if ((err?.data as { message: string })?.message) {
                setSearchError({ msg: (err.data as { message: string }).message, type: 'error' });
            }
            return;
        } finally {
            setSearching(false);
        }

        if (members.some(m => m.id === response.id)) {
            setSearchError({
                msg: t('create_project_group_modal_page.member_already_in_group_warning_msg'),
                type: 'error',
            });
            return;
        }

        const updatedMembers = [...members, getMemberDetailsFromMembershipUserAttrs(response)];
        setMembers(updatedMembers);
        setFieldValue('search', ''); // Clear the input

        if (updatedMembers.length >= project.maxGroupSize!) {
            setSearchError({
                msg: t('create_project_group_modal_page.max_group_size_reached_warning_msg'),
                type: 'warning',
            });
        }
    };

    return (
        <FormProvider {...formFunctions}>
            <form className="flex h-full flex-col">
                <Description project={project} />
                <MemberSearch
                    className="my-7.5"
                    maxGroupSizeReached={members.length >= project.maxGroupSize!}
                    searching={searching}
                    onChange={() => setSearchError(null)}
                    onSearch={onSearch}
                    searchError={searchError}
                />
                <MembersList
                    members={members}
                    showMemberStatus={member => member.detailsStatus === MemberDetailsMembershipStatus.CREATOR}
                    showRemoveMemberButton={member => member.detailsStatus !== MemberDetailsMembershipStatus.CREATOR}
                    onRemove={member => {
                        setMembers(members.filter(m => m.id !== member.id));
                        setSearchError(null);
                    }}
                />
                <FormSubmissionButtons
                    className="mt-12.5"
                    members={members}
                    onCancel={onCancel}
                    onConfirm={onConfirm}
                />
            </form>
        </FormProvider>
    );
};

export const CreateProjectGroupModalPage = memo(CreateProjectGroupFormComponent);
